import { formatDate } from 'utils/helpers/date'
import ActionCellRenderer from './actionCellRenderer'
import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form'

export const getColumns = (
  isEditor: boolean,
  getAggregatorToDelete: (params: ICellRendererParams) => void,
) =>
  [
    { headerName: 'Aggregator Name', field: 'aggregatorName', minWidth: 200 },
    { headerName: 'ID', field: '_id' },
    { headerName: 'User', field: 'username' },
    {
      headerName: 'Creation Date',
      field: 'createdAt',
      valueFormatter: (params: ValueFormatterParams) =>
        formatDate(params.value, { includeTime: true }),
    },
    {
      headerName: 'Status',
      field: 'isActive',
      cellRenderer: (params: ICellRendererParams) => {
        return params?.value === true
          ? 'Active'
          : params?.value === false
            ? 'Inactive'
            : '-'
      },
    },
    {
      headerName: 'FTP Status',
      field: 'ftp.isEnable',
      minWidth: 120,
      cellRenderer: (params: ICellRendererParams) => {
        return params?.value === true
          ? 'Enable'
          : params?.value === false
            ? 'Disable'
            : '-'
      },
    },
    {
      headerName: 'FTP Running',
      field: 'ftp.isRunning',
      minWidth: 120,
      cellRenderer: (params: ICellRendererParams) => {
        return params?.value === true
          ? 'Yes'
          : params?.value === false
            ? 'No'
            : '-'
      },
    },
    {
      headerName: 'API Status',
      field: 'api.isEnable',
      minWidth: 120,
      cellRenderer: (params: ICellRendererParams) => {
        return params?.value === true
          ? 'Enable'
          : params?.value === false
            ? 'Disable'
            : '-'
      },
    },
    {
      hide: !isEditor,
      headerName: 'Action',
      field: 'save',
      minWidth: 200,
      cellRenderer: (params: ICellRendererParams) => (
        <ActionCellRenderer
          params={params}
          isEditor={isEditor}
          getAggregatorToDelete={getAggregatorToDelete}
        />
      ),
    },
  ] as ColDef[]

// Constant: Authentication Types
export const authTypes = {
  BASIC: 'BASIC_AUTH',
  BEARER: 'BEARER_AUTH',
  API_KEY: 'API_KEY_AUTH',
}

// Constant: FTP Protocols
export const FTP_PROTOCOLS = {
  FTP: 'FTP',
  SFTP: 'SFTP',
}

// Options: Status options - Active/Inactive
export const activeOptions = [
  { label: 'Active', value: true, disabled: false },
  { label: 'Inactive', value: false, disabled: false },
]

// Options: Authentication
export const authenticationOptions = [
  { label: 'Bearer Auth', value: authTypes.BEARER, disabled: false },
  { label: 'Basic Auth', value: authTypes.BASIC, disabled: false },
  { label: 'API Key Based Auth', value: authTypes.API_KEY, disabled: false },
]

// Options: FTP Protocol
export const ftpProtocolOptions = [
  { label: 'FTP / FTPS', value: FTP_PROTOCOLS.FTP, disabled: false },
  { label: 'SFTP', value: FTP_PROTOCOLS.SFTP, disabled: false },
]

// Constant: Parent keys for API Call
export const aggParentFields = {
  FTP: 'ftp',
  API: 'api',
  MOBILE: 'mobile',
  SAP: 'sap',
  DELIVEROO: 'deliveroo',
}

export const aggregatorFormFields = {
  // AGGREGATOR
  AGG_NAME: { key: 'aggregatorName', label: 'Aggregator Name' },
  AGG_ID: { key: 'aggregatorId', label: 'Aggregator ID' },
  AGG_IS_ACTIVE: { key: 'isActive', label: 'Active' },
  AGG_GRUBTECH_ID: { key: 'grubtechId', label: 'Grubtech Mapping ID' },
  AUTO_ATTACH_BARCODES: {
    key: 'autoAttachBarcodes',
    label: 'Auto-Attach Barcodes',
  },
  // Aggregator FTP
  FTP_ACTIVE: {
    key: 'isEnable',
    label: 'FTP Status',
    parentKey: aggParentFields.FTP,
  },
  FTP_DELETE_FILES: {
    key: 'deleteFiles',
    label: 'Delete old files ?',
    parentKey: aggParentFields.FTP,
  },
  FTP_ENABLE_S3_STORAGE: {
    key: 'enableS3Storage',
    label: 'Store Files in S3',
    parentKey: aggParentFields.FTP,
  },
  FTP_PROTOCOL: {
    key: 'protocol',
    label: 'Protocol',
    parentKey: aggParentFields.FTP,
  },
  FTP_HOST: {
    key: 'host',
    label: 'Host',
    parentKey: aggParentFields.FTP,
  },
  FTP_PORT: {
    key: 'port',
    label: 'Port',
    parentKey: aggParentFields.FTP,
  },
  FTP_USERNAME: {
    key: 'username',
    label: 'Username',
    parentKey: aggParentFields.FTP,
  },
  FTP_PASSWORD: {
    key: 'password',
    label: 'Password',
    parentKey: aggParentFields.FTP,
  },
  FTP_FILE_GENERATION_LIMIT: {
    key: 'concurrentFileGenerationLimit',
    label: 'File Generation Limit',
    parentKey: aggParentFields.FTP,
  },
  FTP_PATH: {
    key: 'directory',
    label: 'Path',
    parentKey: aggParentFields.FTP,
  },
  FTP_CRON_EXPRESSION: {
    key: 'cronExpression',
    label: 'FTP File Generation Cron Expression',
    parentKey: aggParentFields.FTP,
  },
  FTP_ACTIVATED_STORES: {
    key: 'activatedStores',
    label: 'Activated Stores',
    parentKey: aggParentFields.FTP,
  },
  FTP_MAX_RUNTIME_MINUTES: {
    key: 'maxRuntimeMinutes',
    label: 'Max Runtime (minutes)',
    parentKey: aggParentFields.FTP,
  },
  FTP_MAX_HOURS_WITHOUT_RUN: {
    key: 'maxHoursWithoutRun',
    label: 'Max Hours Without Run',
    parentKey: aggParentFields.FTP,
  },
  // --------------- Aggregator API ---------------
  API_AUTH_TYPE: {
    key: 'authType',
    label: 'Authentication Type',
    parentKey: aggParentFields.API,
  },
  // AUTH - Bearer
  API_AUTH_URL: {
    key: 'authUrl',
    label: 'Login URL',
    parentKey: aggParentFields.API,
  },
  API_AUTH_TOKEN_KEY: {
    key: 'authTokenKey',
    label: 'Token Key',
    parentKey: aggParentFields.API,
  },
  API_AUTH_REQ_SCHEMA: {
    key: 'authRequestSchema',
    label: 'Auth Request Schema',
    parentKey: aggParentFields.API,
  },
  // AUTH - Basic
  API_AUTH_USERNAME: {
    key: 'authUsername',
    label: 'User Name',
    parentKey: aggParentFields.API,
  },
  API_AUTH_PASSWORD: {
    key: 'authPassword',
    label: 'Password',
    parentKey: aggParentFields.API,
  },
  // AUTH - API KEY BASED
  API_AUTH_API_KEY: {
    key: 'authApiKey',
    label: 'Key',
    parentKey: aggParentFields.API,
  },
  API_AUTH_API_VALUE: {
    key: 'authApiValue',
    label: 'Value',
    parentKey: aggParentFields.API,
  },
  // API- Common
  API_ACTIVE: {
    key: 'isEnable',
    label: 'API Status',
    parentKey: aggParentFields.API,
  },
  API_ROOT_SCHEMA: {
    key: 'rootSchema',
    label: 'Root Schema',
    parentKey: aggParentFields.API,
  },
  API_PRODUCT_SCHEMA: {
    key: 'productSchema',
    label: 'Product Schema',
    parentKey: aggParentFields.API,
  },
  API_STORES: {
    key: 'stores',
    label: 'Stores',
    parentKey: aggParentFields.API,
  },
  API_AUTH_TOKEN_EXPIRY: {
    key: 'authTokenExpiry',
    label: 'Auth Token Expiry (in minutes)',
    parentKey: aggParentFields.API,
  },
  API_MAX_CONCURRENT_STORES: {
    key: 'maxConcurrentStores',
    label: 'Max Concurrent Stores',
    parentKey: aggParentFields.API,
  },
  API_ITEMS_PER_REQUEST: {
    key: 'itemsPerRequest',
    label: 'Items Per Request',
    parentKey: aggParentFields.API,
  },
  API_MAX_PARALLEL_ITEM_BATCHES: {
    key: 'maxParallelItemBatches',
    label: 'Max Parallel Item Batches',
    parentKey: aggParentFields.API,
  },

  API_URL: { key: 'url', label: 'API URL', parentKey: aggParentFields.API },

  // --------------- Android POS API ---------------
  BARCODE_REGEX: {
    key: 'barcodeRegex',
    label: 'Barcode Regex',
    parentKey: aggParentFields.MOBILE,
  },
  CUSTOMER_ID: {
    key: 'customerId',
    label: 'Customer Id',
    parentKey: aggParentFields.MOBILE,
  },
  ORDER_TYPES: {
    key: 'orderTypes',
    label: 'Order Types',
    parentKey: aggParentFields.MOBILE,
  },
  IMG_URL: {
    key: 'imgUrl',
    label: 'Image URL',
    parentKey: aggParentFields.MOBILE,
  },

  // --------------- SAP Config ---------------
  SAP_DOC_TYPE: {
    parentKey: aggParentFields.SAP,
    key: 'documentType',
    label: 'Document Type',
  },
  SAP_RMA_DOC_TYPE: {
    parentKey: aggParentFields.SAP,
    key: 'documentTypeRma',
    label: 'RMA Document Type',
  },
  SAP_SALES_ORG: {
    parentKey: aggParentFields.SAP,
    key: 'salesOrganization',
    label: 'Sales Organization',
  },
  SAP_DISTR_CHANNEL: {
    parentKey: aggParentFields.SAP,
    key: 'distributionChannel',
    label: 'Distribution Channel',
  },
  SAP_DIVISION: {
    parentKey: aggParentFields.SAP,
    key: 'division',
    label: 'Division',
  },
  SAP_CUSTOMER_CODE: {
    parentKey: aggParentFields.SAP,
    key: 'customerCode',
    label: 'Customer Code',
  },
  SAP_PARTNERS_LIST: {
    parentKey: aggParentFields.SAP,
    key: 'partnersList',
    label: 'Partners List JSON',
  },
  SAP_SHOULD_SEND_CREDIT_SALE: {
    parentKey: aggParentFields.SAP,
    key: 'shouldSendCreditSaleField',
    label: 'Should Send Credit Sale',
  },
  REQUIRE_PHONE: {
    key: 'requirePhone',
    label: 'Require Customer Phone?',
    parentKey: aggParentFields.MOBILE,
  },
  REQUIRE_ADDRESS: {
    key: 'requireAddress',
    label: 'Require Customer Address?',
    parentKey: aggParentFields.MOBILE,
  },
  REQUIRE_CUSTOMER_NAME: {
    key: 'requireCustomerName',
    label: 'Require Customer Name?',
    parentKey: aggParentFields.MOBILE,
  },
  IS_MANUAL_ENTRY_ENABLE: {
    key: 'isManualEntryEnable',
    label: 'Is manual entry enable',
    parentKey: aggParentFields.MOBILE,
  },
  POS_PAYMENTS_MODE: {
    key: 'posPaymentMode',
    label: 'Pos Payment Modes',
    parentKey: aggParentFields.MOBILE,
  },
  IS_DELIVERY_FEE_ENABLE: {
    key: 'isDeliveryFeeEnable',
    label: 'Is delivery fee enable',
    parentKey: aggParentFields.MOBILE,
  },
  IS_DELIVERY_FEE_MANDATORY: {
    key: 'isDeliveryFeeMandatory',
    label: 'Is delivery fee mandatory',
    parentKey: aggParentFields.MOBILE,
  },
  DELIVERY_FEE_AMOUNT: {
    key: 'deliveryFeeAmount',
    label: 'Delivery Fee Amount (AED)',
    parentKey: aggParentFields.MOBILE,
  },
  KEYBOARD_CONFIGURATION: {
    key: 'keyboardType',
    label: 'Keyboard Type',
    parentKey: aggParentFields.MOBILE,
  },
  ORDER_NUM_LENGTH: {
    key: 'orderNumLength',
    label: 'Order Num Length',
    parentKey: aggParentFields.MOBILE,
  },
  RECONCILIATION_PAYMENT_TYPES: {
    key: 'reconciliationPaymentTypes',
    parentKey: aggParentFields.MOBILE,
  },
  ENABLE_PICKER_ASSIGNMENT: {
    key: 'enablePickerAssignment',
    label: 'Enable picker assignment on print',
    parentKey: aggParentFields.MOBILE,
  },
  ALLOW_ITEM_REPLACEMENT: {
    key: 'allowItemReplacement',
    label: 'Allow item replacement',
    parentKey: aggParentFields.MOBILE,
  },
} as const

export const DELIVEROO_FIELDS = {
  IS_ENABLE: {
    parentKey: aggParentFields.DELIVEROO,
    key: 'isEnable',
    label: 'Deliveroo API Status',
  },
  BASE_URL: {
    parentKey: aggParentFields.DELIVEROO,
    key: 'baseUrl',
    label: 'Deliveroo API Base URL',
  },
  AUTH_BASE_URL: {
    parentKey: aggParentFields.DELIVEROO,
    key: 'authBaseUrl',
    label: 'Deliveroo Auth Base URL',
  },
  WEBHOOK_SECRET: {
    parentKey: aggParentFields.DELIVEROO,
    key: 'webhookSecret',
    label: 'Deliveroo Webhook Secret',
  },
  BRAND_ID: {
    parentKey: aggParentFields.DELIVEROO,
    key: 'brandId',
    label: 'Deliveroo Brand ID',
  },
  CATALOGUE_ID: {
    parentKey: aggParentFields.DELIVEROO,
    key: 'catalogueId',
    label: 'Deliveroo Catalogue ID',
  },
  USERNAME: {
    parentKey: aggParentFields.DELIVEROO,
    key: 'username',
    label: 'Username',
  },
  PASSWORD: {
    parentKey: aggParentFields.DELIVEROO,
    key: 'password',
    label: 'Password',
  },
  DEFAULT_PRICE_STORE_ID: {
    parentKey: aggParentFields.DELIVEROO,
    key: 'defaultPriceStoreId',
    label: 'Default Price Store',
  },
  HERO_IMAGE_URL: {
    parentKey: aggParentFields.DELIVEROO,
    key: 'heroImageUrl',
    label: 'Hero Image URL',
  },
}

export const getNestErrorMessage = (
  errors: Record<string, any>,
  obj: Record<string, any>,
): string => {
  return errors?.[obj.parentKey]?.[obj.key]?.message
}

export interface CommonFormType {
  register: UseFormRegister<any>
  errors: FieldErrors<any>
  setValue: UseFormSetValue<any>
  aggregator?: any
  ftpIsActive?: boolean
  apiIsActive?: boolean
}
